import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FeatherIcon from 'feather-icons-react';
import {
  acknowledgeNotification,
  fetchNotificationEvents,
  fetchNotificationRoleAssignments,
  fetchNotifications,
  formatNotificationTime,
  markAllNotificationsRead,
  markNotificationRead,
  resolveNotificationLinkUrl,
} from '../../service/api/notification-service';
import { startNotificationHub } from '../../service/api/notification-hub';
import { showToast } from 'Core/redux/toast/slice';
import './notifications.css';

const FILTERS = [
  { id: 'all', label: 'All', icon: 'inbox' },
  { id: 'unread', label: 'Unread', icon: 'mail' },
  { id: 'needsAck', label: 'Needs acknowledgement', icon: 'alert-circle' },
  { id: 'acknowledged', label: 'Acknowledged', icon: 'check-circle' },
];

const FILTER_BY_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'category', label: 'Category' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All dates' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'month', label: 'Last one month' },
  { value: 'custom', label: 'Custom dates' },
];

const FALLBACK_CATEGORIES = ['WMS', 'QMS', 'MMS', 'PPMS'];
const SYSTEM_USER_OPTION = { value: 'system', label: 'System' };

const PAGE_SIZE = 20;

const FILTER_SELECT_STYLES = {
  control: base => ({
    ...base,
    minHeight: 38,
    borderColor: '#e5e7eb',
    boxShadow: 'none',
    '&:hover': { borderColor: '#d1d5db' },
  }),
  valueContainer: base => ({
    ...base,
    paddingLeft: 8,
    paddingRight: 8,
  }),
  menu: base => ({
    ...base,
    zIndex: 6,
  }),
  menuPortal: base => ({ ...base, zIndex: 1060 }),
};

/** Deterministic accent color per module/category so chips are visually distinct. */
const CATEGORY_COLORS = ['#1a56db', '#0e9f6e', '#c27803', '#e02424', '#7e3af2', '#0694a2'];
function categoryColor(category) {
  const key = (category || 'N').toUpperCase();
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length];
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function resolveDateRange(preset, customFrom, customTo) {
  const now = new Date();
  if (preset === 'last7') {
    const from = startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
    return { fromDate: from.toISOString(), toDate: endOfLocalDay(now).toISOString() };
  }
  if (preset === 'month') {
    const from = startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), 1));
    return { fromDate: from.toISOString(), toDate: endOfLocalDay(now).toISOString() };
  }
  if (preset === 'custom') {
    const next = {};
    if (customFrom) next.fromDate = startOfLocalDay(customFrom).toISOString();
    if (customTo) next.toDate = endOfLocalDay(customTo).toISOString();
    return next;
  }
  return {};
}

function buildUserOptions(assignments) {
  const byValue = new Map();
  byValue.set(SYSTEM_USER_OPTION.value, SYSTEM_USER_OPTION);
  (Array.isArray(assignments) ? assignments : []).forEach(row => {
    const email = String(row?.userEmail || '').trim();
    const name = String(row?.userDisplayName || '').trim();
    const value = email || name;
    if (!value) return;
    const key = value.toLowerCase();
    if (byValue.has(key) || key === 'system') return;
    const label =
      name && email && name.toLowerCase() !== email.toLowerCase()
        ? `${name} (${email})`
        : name || email;
    byValue.set(key, { value, label });
  });
  const users = [...byValue.values()]
    .filter(option => option.value !== 'system')
    .sort((a, b) => a.label.localeCompare(b.label));
  return [SYSTEM_USER_OPTION, ...users];
}

function buildCategoryOptions(events) {
  const fromEvents = [
    ...new Set(
      (Array.isArray(events) ? events : [])
        .map(evt => String(evt?.category || '').trim())
        .filter(Boolean),
    ),
  ];
  const values = fromEvents.length ? fromEvents : FALLBACK_CATEGORIES;
  return [...new Set(values)]
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ value, label: value }));
}

const NotificationsView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filteredUnreadCount, setFilteredUnreadCount] = useState(0);
  const [needsAckCount, setNeedsAckCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterBy, setFilterBy] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [userOptions, setUserOptions] = useState([SYSTEM_USER_OPTION]);
  const [categoryOptions, setCategoryOptions] = useState(
    FALLBACK_CATEGORIES.map(value => ({ value, label: value })),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE)),
    [totalCount],
  );

  const extraFilters = useMemo(() => {
    const next = { ...resolveDateRange(datePreset, customFrom, customTo) };
    if (filterBy === 'user' && filterValue) next.createdBy = filterValue;
    if (filterBy === 'category' && filterValue) next.category = filterValue;
    return next;
  }, [filterBy, filterValue, datePreset, customFrom, customTo]);

  const hasNarrowingFilters = useMemo(
    () =>
      filter === 'needsAck' ||
      filter === 'acknowledged' ||
      Boolean(search) ||
      Boolean(extraFilters.category) ||
      Boolean(extraFilters.createdBy) ||
      Boolean(extraFilters.fromDate) ||
      Boolean(extraFilters.toDate),
    [filter, search, extraFilters],
  );

  const inboxFilterOptions = useMemo(
    () => ({
      filter,
      search: search || undefined,
      category: extraFilters.category,
      createdBy: extraFilters.createdBy,
      fromDate: extraFilters.fromDate,
      toDate: extraFilters.toDate,
    }),
    [filter, search, extraFilters],
  );

  const valueOptions = filterBy === 'user' ? userOptions : filterBy === 'category' ? categoryOptions : [];
  const selectedFilterBy = FILTER_BY_OPTIONS.find(option => option.value === filterBy) || null;
  const selectedFilterValue = valueOptions.find(option => option.value === filterValue) || null;
  const selectedDatePreset = DATE_RANGE_OPTIONS.find(option => option.value === datePreset) || DATE_RANGE_OPTIONS[0];

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotifications({
        page,
        pageSize: PAGE_SIZE,
        filter,
        search: search || undefined,
        category: extraFilters.category,
        createdBy: extraFilters.createdBy,
        fromDate: extraFilters.fromDate,
        toDate: extraFilters.toDate,
      });
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
      setUnreadCount(data.unreadCount || 0);
      setFilteredUnreadCount(data.filteredUnreadCount ?? data.unreadCount ?? 0);
      setNeedsAckCount(data.needsAcknowledgementCount || 0);
    } catch (err) {
      console.error('Failed to load notifications page', err);
      setError(err.message || 'Failed to load notifications');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter, search, extraFilters]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(next);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    let cancelled = false;
    fetchNotificationEvents()
      .then(events => {
        if (!cancelled) setCategoryOptions(buildCategoryOptions(events));
      })
      .catch(err => {
        console.warn('Failed to load notification categories', err);
      });
    fetchNotificationRoleAssignments()
      .then(assignments => {
        if (!cancelled) setUserOptions(buildUserOptions(assignments));
      })
      .catch(err => {
        console.warn('Failed to load notification users', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    startNotificationHub(dto => {
      if (!dto || !dto.notificationId) return;
      loadPage();
    })
      .then(result => {
        if (cancelled) {
          result?.unsubscribe?.();
          return;
        }
        unsubscribe = result?.unsubscribe || (() => {});
      })
      .catch(err => {
        console.warn('SignalR unavailable on notifications page', err);
      });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [loadPage]);

  const handleSearchSubmit = e => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleFilterChange = nextFilter => {
    setFilter(nextFilter);
    setPage(1);
  };

  const handleFilterByChange = option => {
    setFilterBy(option?.value || null);
    setFilterValue(null);
    setPage(1);
  };

  const handleFilterValueChange = option => {
    setFilterValue(option?.value || null);
    setPage(1);
  };

  const handleDatePresetChange = option => {
    setDatePreset(option?.value || 'all');
    setPage(1);
  };

  const handleCustomFromChange = date => {
    setCustomFrom(date);
    setPage(1);
  };

  const handleCustomToChange = date => {
    setCustomTo(date);
    setPage(1);
  };

  const handleRowClick = async item => {
    try {
      if (!item.isRead) {
        await markNotificationRead(item.notificationId);
        setItems(prev =>
          prev.map(n =>
            n.notificationId === item.notificationId
              ? { ...n, isRead: true, readDate: new Date().toISOString() }
              : n,
          ),
        );
        setUnreadCount(c => Math.max(0, c - 1));
        setFilteredUnreadCount(c => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Mark read failed', err);
    }
    const linkUrl = resolveNotificationLinkUrl(item);
    if (linkUrl) {
      navigate(linkUrl);
    }
  };

  const handleAcknowledge = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await acknowledgeNotification(item.notificationId);
      setItems(prev =>
        prev.map(n =>
          n.notificationId === item.notificationId
            ? {
                ...n,
                isRead: true,
                isAcknowledged: true,
                acknowledgedDate: new Date().toISOString(),
              }
            : n,
        ),
      );
      setNeedsAckCount(c => Math.max(0, c - 1));
      if (!item.isRead) {
        setUnreadCount(c => Math.max(0, c - 1));
        setFilteredUnreadCount(c => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Acknowledge failed', err);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      const result = await markAllNotificationsRead(inboxFilterOptions);
      const updatedCount = Number(result?.updatedCount || 0);
      await loadPage();
      if (updatedCount <= 0) {
        dispatch(
          showToast({
            message: hasNarrowingFilters
              ? 'No unread notifications in this view.'
              : 'No unread notifications to mark.',
            type: 'info',
          }),
        );
      } else {
        dispatch(
          showToast({
            message: hasNarrowingFilters
              ? `Marked ${updatedCount} filtered notification${updatedCount === 1 ? '' : 's'} as read.`
              : `Marked ${updatedCount} notification${updatedCount === 1 ? '' : 's'} as read.`,
            type: 'success',
          }),
        );
      }
    } catch (err) {
      console.error('Mark all read failed', err);
      dispatch(
        showToast({
          message: err.message || 'Failed to mark notifications as read.',
          type: 'danger',
        }),
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);
  const selectMenuPortal = typeof document !== 'undefined' ? document.body : null;

  return (
    <div className="page-wrapper">
      <div className="content notifications-page">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Notifications</h4>
              <h6>Your inbox — read, acknowledge, and open related items</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap justify-content-end">
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={handleMarkAllRead}
              disabled={filteredUnreadCount === 0 || markingAll}
              title={
                hasNarrowingFilters
                  ? 'Mark unread notifications in the current filter as read'
                  : 'Mark all unread notifications as read'
              }
            >
              {markingAll ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <FeatherIcon icon="check-square" size={16} />
              )}
              {markingAll
                ? 'Marking…'
                : hasNarrowingFilters
                  ? 'Mark filtered as read'
                  : 'Mark all as read'}
            </button>
          </div>
        </div>

        {/* Summary stat cards */}
        <div className="row g-3 mb-1 notifications-stats">
          <div className="col-sm-4">
            <div className="noti-stat-card">
              <span className="noti-stat-icon" style={{ background: '#eef2ff', color: '#1a56db' }}>
                <FeatherIcon icon="inbox" size={20} />
              </span>
              <div>
                <div className="noti-stat-value">{totalCount}</div>
                <div className="noti-stat-label">All matching</div>
              </div>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="noti-stat-card">
              <span className="noti-stat-icon" style={{ background: '#fef3f2', color: '#e02424' }}>
                <FeatherIcon icon="mail" size={20} />
              </span>
              <div>
                <div className="noti-stat-value">{unreadCount}</div>
                <div className="noti-stat-label">Unread</div>
              </div>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="noti-stat-card">
              <span className="noti-stat-icon" style={{ background: '#fff8eb', color: '#c27803' }}>
                <FeatherIcon icon="alert-circle" size={20} />
              </span>
              <div>
                <div className="noti-stat-value">{needsAckCount}</div>
                <div className="noti-stat-label">Needs acknowledgement</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card notifications-card">
          <div className="card-body">
            {/* Toolbar: filters + search */}
            <div className="notifications-toolbar">
              <div className="notifications-filter-tabs">
                {FILTERS.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className={`noti-filter-tab ${filter === f.id ? 'active' : ''}`}
                    onClick={() => handleFilterChange(f.id)}
                  >
                    <FeatherIcon icon={f.icon} size={14} />
                    <span>{f.label}</span>
                    {f.id === 'unread' && unreadCount > 0 && (
                      <span className="noti-filter-count">{unreadCount}</span>
                    )}
                    {f.id === 'needsAck' && needsAckCount > 0 && (
                      <span className="noti-filter-count warning">{needsAckCount}</span>
                    )}
                  </button>
                ))}
              </div>
              <form className="noti-search" onSubmit={handleSearchSubmit}>
                <FeatherIcon icon="search" size={15} className="noti-search-icon" />
                <input
                  type="search"
                  placeholder="Search title, ticket, part…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button type="button" className="noti-search-clear" onClick={handleClearSearch}>
                    <FeatherIcon icon="x" size={14} />
                  </button>
                )}
              </form>
            </div>

            <div className="notifications-advanced-filters">
              <div className="noti-filter-field">
                <label htmlFor="noti-filter-by">Filter by</label>
                <Select
                  inputId="noti-filter-by"
                  classNamePrefix="react-select"
                  options={FILTER_BY_OPTIONS}
                  value={selectedFilterBy}
                  onChange={handleFilterByChange}
                  placeholder="User or Category"
                  isClearable
                  isSearchable={false}
                  styles={FILTER_SELECT_STYLES}
                  menuPortalTarget={selectMenuPortal}
                />
              </div>
              <div className="noti-filter-field">
                <label htmlFor="noti-filter-value">
                  {filterBy === 'user' ? 'User' : filterBy === 'category' ? 'Category' : 'Value'}
                </label>
                <Select
                  inputId="noti-filter-value"
                  classNamePrefix="react-select"
                  options={valueOptions}
                  value={selectedFilterValue}
                  onChange={handleFilterValueChange}
                  placeholder={
                    filterBy === 'user'
                      ? 'All users'
                      : filterBy === 'category'
                        ? 'All categories'
                        : 'Select a filter first'
                  }
                  isClearable
                  isSearchable
                  isDisabled={!filterBy}
                  styles={FILTER_SELECT_STYLES}
                  menuPortalTarget={selectMenuPortal}
                  noOptionsMessage={() => (filterBy === 'user' ? 'No users found' : 'No categories found')}
                />
              </div>
              <div className="noti-filter-field">
                <label htmlFor="noti-date-range">Date range</label>
                <Select
                  inputId="noti-date-range"
                  classNamePrefix="react-select"
                  options={DATE_RANGE_OPTIONS}
                  value={selectedDatePreset}
                  onChange={handleDatePresetChange}
                  isSearchable={false}
                  styles={FILTER_SELECT_STYLES}
                  menuPortalTarget={selectMenuPortal}
                />
              </div>
              {datePreset === 'custom' && (
                <>
                  <div className="noti-filter-field noti-filter-date">
                    <label htmlFor="noti-date-from">From</label>
                    <DatePicker
                      id="noti-date-from"
                      selected={customFrom}
                      onChange={handleCustomFromChange}
                      selectsStart
                      startDate={customFrom}
                      endDate={customTo}
                      maxDate={customTo || undefined}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="MM/DD/YYYY"
                      autoComplete="off"
                      isClearable
                    />
                  </div>
                  <div className="noti-filter-field noti-filter-date">
                    <label htmlFor="noti-date-to">To</label>
                    <DatePicker
                      id="noti-date-to"
                      selected={customTo}
                      onChange={handleCustomToChange}
                      selectsEnd
                      startDate={customFrom}
                      endDate={customTo}
                      minDate={customFrom || undefined}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="MM/DD/YYYY"
                      autoComplete="off"
                      isClearable
                    />
                  </div>
                </>
              )}
            </div>

            {/* States */}
            {loading && (
              <div className="noti-placeholder">
                <div className="spinner-border text-primary" role="status" aria-hidden="true" />
                <p>Loading notifications…</p>
              </div>
            )}

            {!loading && error && (
              <div className="noti-placeholder text-danger">
                <FeatherIcon icon="alert-triangle" size={28} />
                <p>{error}</p>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadPage}>
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && items.length === 0 && (
              <div className="noti-placeholder">
                <FeatherIcon icon="bell-off" size={30} />
                <p>No notifications match this view.</p>
              </div>
            )}

            {/* List */}
            {!loading && !error && items.length > 0 && (
              <ul className="noti-list">
                {items.map(item => {
                  const needsAck = item.requiresAck && !item.isAcknowledged;
                  return (
                    <li
                      key={item.recipientId || item.notificationId}
                      className={`noti-item ${item.isRead ? '' : 'unread'} ${resolveNotificationLinkUrl(item) ? 'clickable' : ''}`}
                      onClick={() => handleRowClick(item)}
                    >
                      <span
                        className="noti-avatar"
                        style={{ background: categoryColor(item.category) }}
                        title={item.category}
                      >
                        {(item.category || 'N').slice(0, 3)}
                      </span>

                      <div className="noti-main">
                        <div className="noti-title-row">
                          <span className="noti-title">{item.title}</span>
                          {!item.isRead && <span className="noti-dot" title="Unread" />}
                        </div>
                        <p className="noti-message">{item.message}</p>
                        <div className="noti-meta">
                          <span className="noti-time">
                            <FeatherIcon icon="clock" size={12} />
                            {formatNotificationTime(item.createdDate)}
                          </span>
                          {needsAck && <span className="noti-pill warning">Needs ack</span>}
                          {item.isAcknowledged && (
                            <span className="noti-pill success">Acknowledged</span>
                          )}
                          {!item.isRead && <span className="noti-pill primary">Unread</span>}
                        </div>
                      </div>

                      <div className="noti-actions d-flex align-items-center gap-2">
                        {needsAck && (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={e => handleAcknowledge(e, item)}
                          >
                            Acknowledge
                          </button>
                        )}
                        {resolveNotificationLinkUrl(item) ? (
                          <FeatherIcon icon="chevron-right" size={18} className="text-muted" />
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Footer / pagination */}
            {!loading && !error && totalCount > 0 && (
              <div className="noti-pagination">
                <span className="text-muted small">
                  Showing {rangeStart}–{rangeEnd} of {totalCount}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <FeatherIcon icon="chevron-left" size={14} />
                    Previous
                  </button>
                  <span className="small text-muted">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                    <FeatherIcon icon="chevron-right" size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsView;
